import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, 
  ClipboardList, 
  Calendar, 
  Users, 
  Eye, 
  Trash2, 
  Edit, 
  Search, 
  Filter,
  Send,
  Paperclip,
  AlertCircle,
  CheckCircle,
  Clock,
  Upload,
  X,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-800',
  Medium: 'bg-yellow-100 text-yellow-800',
  Low: 'bg-green-100 text-green-800'
};

const STATUS_COLORS = {
  Open: 'bg-slate-100 text-slate-800',
  Assigned: 'bg-blue-100 text-blue-800',
  'In Progress': 'bg-purple-100 text-purple-800',
  Completed: 'bg-green-100 text-green-800',
  Cancelled: 'bg-red-100 text-red-800'
};

const INTERESTS = [
  "Administrative Support",
  "Event Support",
  "Bookshelf Assembly Team",
  "Book Drives & Sorting",
  "Delivery & Installation",
  "Community Outreach",
  "Photography/Videography",
  "Grant Writing",
  "Social Media",
  "Fundraising",
  "Board/Leadership",
  "Other"
];

export default function TaskManagement() {
  const [tasks, setTasks] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [interestFilter, setInterestFilter] = useState('All');
  
  // Create Task Dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    related_interest: '',
    due_date: '',
    priority: 'Medium',
    attachments: []
  });
  
  // Task Detail Dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // Assign Volunteers Dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedVolunteers, setSelectedVolunteers] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [matchingVolunteers, setMatchingVolunteers] = useState([]);
  
  // Delete Dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, volunteersRes] = await Promise.all([
        fetch(`${API}/admin/tasks`, { credentials: 'include' }),
        fetch(`${API}/volunteers?status=Approved`, { credentials: 'include' })
      ]);

      if (tasksRes.ok) {
        const data = await tasksRes.json();
        setTasks(data);
      }
      if (volunteersRes.ok) {
        const data = await volunteersRes.json();
        setVolunteers(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    if (!taskForm.title || !taskForm.description || !taskForm.related_interest) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${API}/admin/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(taskForm)
      });

      if (response.ok) {
        toast.success('Task created successfully');
        setShowCreateDialog(false);
        resetTaskForm();
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const deleteTask = async () => {
    if (!selectedTask) return;

    try {
      const response = await fetch(`${API}/admin/tasks/${selectedTask.task_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Task deleted');
        setShowDeleteDialog(false);
        setShowDetailDialog(false);
        setSelectedTask(null);
        fetchData();
      } else {
        toast.error('Failed to delete task');
      }
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const openAssignDialog = (task) => {
    setSelectedTask(task);
    setSelectedVolunteers([]);
    setCustomMessage('');
    setSendNotification(true);
    
    // Filter volunteers who have the matching interest
    const matching = volunteers.filter(v => 
      v.volunteer_roles?.includes(task.related_interest) &&
      !task.assigned_volunteers?.includes(v.volunteer_id)
    );
    setMatchingVolunteers(matching);
    setShowAssignDialog(true);
  };

  const assignVolunteers = async () => {
    if (selectedVolunteers.length === 0) {
      toast.error('Please select at least one volunteer');
      return;
    }

    setAssigning(true);
    try {
      const response = await fetch(`${API}/admin/tasks/${selectedTask.task_id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          volunteer_ids: selectedVolunteers,
          send_notification: sendNotification,
          custom_message: customMessage || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowAssignDialog(false);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign volunteers');
      }
    } catch (error) {
      toast.error('Failed to assign volunteers');
    } finally {
      setAssigning(false);
    }
  };

  const unassignVolunteer = async (volunteerId) => {
    if (!selectedTask) return;

    try {
      const response = await fetch(`${API}/admin/tasks/${selectedTask.task_id}/unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify([volunteerId])
      });

      if (response.ok) {
        toast.success('Volunteer unassigned');
        // Refresh task details
        const taskRes = await fetch(`${API}/admin/tasks/${selectedTask.task_id}`, { credentials: 'include' });
        if (taskRes.ok) {
          const updatedTask = await taskRes.json();
          setSelectedTask(updatedTask);
        }
        fetchData();
      } else {
        toast.error('Failed to unassign volunteer');
      }
    } catch (error) {
      toast.error('Failed to unassign volunteer');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      const response = await fetch(`${API}/admin/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success('Task status updated');
        fetchData();
        if (selectedTask?.task_id === taskId) {
          setSelectedTask({ ...selectedTask, status });
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      related_interest: '',
      due_date: '',
      priority: 'Medium',
      attachments: []
    });
  };

  const handleFileUpload = (e) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setTaskForm(prev => ({
          ...prev,
          attachments: [...prev.attachments, {
            filename: file.name,
            file_type: file.type,
            file_data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setTaskForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const openTaskDetail = async (task) => {
    try {
      const response = await fetch(`${API}/admin/tasks/${task.task_id}`, { credentials: 'include' });
      if (response.ok) {
        const fullTask = await response.json();
        setSelectedTask(fullTask);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load task details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No due date';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = 
      task.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || task.status === statusFilter;
    const matchesInterest = interestFilter === 'All' || task.related_interest === interestFilter;
    return matchesSearch && matchesStatus && matchesInterest;
  });

  // Stats
  const stats = {
    total: tasks.length,
    open: tasks.filter(t => t.status === 'Open').length,
    assigned: tasks.filter(t => t.status === 'Assigned').length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    completed: tasks.filter(t => t.status === 'Completed').length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="task-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Task Management
            </h1>
            <p className="text-slate-500 mt-1">Create and assign tasks to volunteers</p>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
            data-testid="create-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Task
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <ClipboardList className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{stats.total}</p>
              <p className="text-xs text-slate-500">Total Tasks</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{stats.open}</p>
              <p className="text-xs text-slate-500">Open</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{stats.assigned}</p>
              <p className="text-xs text-slate-500">Assigned</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{stats.inProgress}</p>
              <p className="text-xs text-slate-500">In Progress</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-md">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bold text-[#1a3a5c]">{stats.completed}</p>
              <p className="text-xs text-slate-500">Completed</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search tasks..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-tasks"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Status</SelectItem>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="Assigned">Assigned</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={interestFilter} onValueChange={setInterestFilter}>
                <SelectTrigger className="w-52" data-testid="interest-filter">
                  <SelectValue placeholder="Filter by Interest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Interests</SelectItem>
                  {INTERESTS.map(interest => (
                    <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Table */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-0">
            {filteredTasks.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Task</TableHead>
                    <TableHead className="font-semibold">Interest Area</TableHead>
                    <TableHead className="font-semibold">Priority</TableHead>
                    <TableHead className="font-semibold">Due Date</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Assigned</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map(task => (
                    <TableRow key={task.task_id} className="hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#1a3a5c]">{task.title}</p>
                          <p className="text-xs text-slate-500 truncate max-w-xs">{task.description}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{task.related_interest}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={PRIORITY_COLORS[task.priority]}>{task.priority}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{formatDate(task.due_date)}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[task.status]}>{task.status}</Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {task.assigned_volunteers?.length || 0} volunteer(s)
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openTaskDetail(task)} data-testid={`view-task-${task.task_id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openAssignDialog(task)} data-testid={`assign-task-${task.task_id}`}>
                            <UserPlus className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No tasks found</p>
                <Button 
                  variant="link" 
                  onClick={() => setShowCreateDialog(true)}
                  className="mt-2"
                >
                  Create your first task
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Task Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#1a3a5c]" />
                Create New Task
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="task-title">Title *</Label>
                <Input
                  id="task-title"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter task title"
                  data-testid="task-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="task-description">Description/Instructions *</Label>
                <Textarea
                  id="task-description"
                  value={taskForm.description}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Provide detailed instructions for this task..."
                  rows={4}
                  data-testid="task-description-input"
                />
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Related Interest *</Label>
                  <Select 
                    value={taskForm.related_interest} 
                    onValueChange={(value) => setTaskForm(prev => ({ ...prev, related_interest: value }))}
                  >
                    <SelectTrigger data-testid="task-interest-select">
                      <SelectValue placeholder="Select interest area" />
                    </SelectTrigger>
                    <SelectContent>
                      {INTERESTS.map(interest => (
                        <SelectItem key={interest} value={interest}>{interest}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={taskForm.priority} 
                    onValueChange={(value) => setTaskForm(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="task-due-date">Due Date (optional)</Label>
                <Input
                  id="task-due-date"
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="task-due-date-input"
                />
              </div>
              
              {/* Attachments */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="task-attachments"
                    data-testid="task-attachments-input"
                  />
                  <label htmlFor="task-attachments" className="cursor-pointer flex flex-col items-center">
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">Click to upload files</span>
                    <span className="text-xs text-slate-400 mt-1">Max 10MB per file</span>
                  </label>
                </div>
                
                {taskForm.attachments.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {taskForm.attachments.map((att, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{att.filename}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => removeAttachment(index)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={createTask} 
                  disabled={creating}
                  className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  data-testid="confirm-create-task"
                >
                  {creating ? 'Creating...' : 'Create Task'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Task Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-[#1a3a5c]" />
                Task Details
              </DialogTitle>
            </DialogHeader>
            {selectedTask && (
              <div className="space-y-4 pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-semibold text-[#1a3a5c]">{selectedTask.title}</h3>
                    <Badge variant="outline" className="mt-1">{selectedTask.related_interest}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={PRIORITY_COLORS[selectedTask.priority]}>{selectedTask.priority}</Badge>
                    <Badge className={STATUS_COLORS[selectedTask.status]}>{selectedTask.status}</Badge>
                  </div>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-2">Description</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedTask.description}</p>
                </div>
                
                {selectedTask.due_date && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="w-4 h-4" />
                    <span>Due: {formatDate(selectedTask.due_date)}</span>
                  </div>
                )}
                
                {/* Attachments */}
                {selectedTask.attachments?.length > 0 && (
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Attachments</h4>
                    <div className="space-y-2">
                      {selectedTask.attachments.map((att, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                          <Paperclip className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{att.filename}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Assigned Volunteers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-slate-700">Assigned Volunteers ({selectedTask.assigned_volunteer_details?.length || 0})</h4>
                    <Button size="sm" variant="outline" onClick={() => openAssignDialog(selectedTask)}>
                      <UserPlus className="w-4 h-4 mr-1" />
                      Assign More
                    </Button>
                  </div>
                  {selectedTask.assigned_volunteer_details?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedTask.assigned_volunteer_details.map(vol => (
                        <div key={vol.volunteer_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium">{vol.first_name} {vol.last_name}</p>
                            <p className="text-xs text-slate-500">{vol.email}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            onClick={() => unassignVolunteer(vol.volunteer_id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No volunteers assigned yet</p>
                  )}
                </div>
                
                {/* Status Update */}
                <div className="flex gap-2 pt-4 border-t">
                  <Select value={selectedTask.status} onValueChange={(status) => updateTaskStatus(selectedTask.task_id, status)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open</SelectItem>
                      <SelectItem value="Assigned">Assigned</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Assign Volunteers Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[#1a3a5c]" />
                Assign Volunteers
              </DialogTitle>
              <DialogDescription>
                Assign volunteers to: <strong>{selectedTask?.title}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Showing volunteers who listed <strong>{selectedTask?.related_interest}</strong> as an interest.
                </p>
              </div>
              
              {matchingVolunteers.length > 0 ? (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {matchingVolunteers.map(vol => (
                    <div 
                      key={vol.volunteer_id}
                      className="flex items-center gap-3 p-3 border rounded hover:bg-slate-50"
                    >
                      <Checkbox
                        id={`vol-${vol.volunteer_id}`}
                        checked={selectedVolunteers.includes(vol.volunteer_id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedVolunteers(prev => [...prev, vol.volunteer_id]);
                          } else {
                            setSelectedVolunteers(prev => prev.filter(id => id !== vol.volunteer_id));
                          }
                        }}
                      />
                      <label htmlFor={`vol-${vol.volunteer_id}`} className="flex-1 cursor-pointer">
                        <p className="font-medium">{vol.first_name} {vol.last_name}</p>
                        <p className="text-xs text-slate-500">{vol.email}</p>
                      </label>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No matching volunteers found with this interest area.
                </p>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="custom-message">Custom Message (optional)</Label>
                <Textarea
                  id="custom-message"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Add a personal note to the notification email..."
                  rows={3}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-notification"
                  checked={sendNotification}
                  onCheckedChange={setSendNotification}
                />
                <label htmlFor="send-notification" className="text-sm cursor-pointer">
                  Send email notification to volunteers
                </label>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={assignVolunteers} 
                  disabled={assigning || selectedVolunteers.length === 0}
                  className="flex-1 bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                >
                  {assigning ? 'Assigning...' : `Assign (${selectedVolunteers.length})`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-red-600">Delete Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-slate-600">
                Are you sure you want to delete "<strong>{selectedTask?.title}</strong>"? 
                This will also remove the task from all assigned volunteers.
              </p>
              <p className="text-sm text-red-600">This action cannot be undone.</p>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={deleteTask} className="flex-1 bg-red-600 hover:bg-red-700">
                  Delete Task
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
